export interface LanguageDetails {
  nameEnglish: string;
  code: LanguageCode;
  countries: Country[];
  isRightToLeft?: boolean;
  translations: { [key: string]: string };
}

export type LanguageCode = string;

export type DefaultLanguageCode = LanguageCode;

export const DefaultLanguageCode: DefaultLanguageCode = 'Default';

export type Country = string;

export type LanguagesConfigurations = Map<LanguageCode, LanguageConfiguration>;

export interface LanguageConfiguration {
  nameEnglish: string;
  countries: Country[];
  translations: { [key: string]: string };
}

export const defaultLanguage: LanguageDetails = {
  nameEnglish: 'Default',
  code: 'Default',
  countries: [],
  translations: {
    submit: 'Submit',
    specify: 'Please specify',
    openAnswerError: 'You can’t save an empty answer option. Please specify the answer or uncheck the option.'
  }
};

export const arabicLanguageDetails: LanguageDetails = {
  nameEnglish: 'Arabic',
  code: 'AR',
  countries: ['Egypt', 'Israel', 'Morocco', 'Saudi Arabia', 'UAE'],
  translations: { submit: 'إرسال', specify: 'من فضلك حدد', openAnswerError: 'يمكنك حفظ خيار إجابة فارغ. يرجى تحديد الإجابة أو إلغاء تحديد الخيار.' },
  isRightToLeft: true
}; // Used in testing

export const languageData: LanguageDetails[] = [
  defaultLanguage,
  arabicLanguageDetails,
  {
    nameEnglish: 'Flemish',
    code: 'BE-FL',
    countries: ['Belgium'],
    translations: {
      submit: 'indienen',
      specify: 'Gelieve te specificeren',
      openAnswerError: 'Je kunt een leeg antwoordoptie niet opslaan. Gelieve het antwoord te specificeren of de optie uit te vinken.'
    }
  },
  {
    nameEnglish: 'Belgian French',
    code: 'BE-FR',
    countries: ['Belgium'],
    translations: {
      submit: 'Envoyer',
      specify: 'Veuillez spécifier',
      openAnswerError: `Vous ne pouvez pas enregistrer une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Canadian French',
    code: 'CA-FR',
    countries: ['Canada'],
    translations: {
      submit: 'Envoyer',
      specify: 'Veuillez spécifier',
      openAnswerError: `Vous ne pouvez pas sauvegarder une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Danish',
    code: 'DA',
    countries: ['Denmark'],
    translations: {
      submit: 'indsend',
      specify: 'Venligst specificer',
      openAnswerError: 'Du kan ikke gemme en tom svarmulighed. Venligst specificer svaret eller fjern markeringen af muligheden.'
    }
  },
  {
    nameEnglish: 'German',
    code: 'DE',
    countries: ['Austria', 'Germany', 'Switzerland'],
    translations: {
      submit: 'Absenden',
      specify: 'Bitte angeben',
      openAnswerError: 'Sie können keine leere Antwortoption speichern. Bitte geben Sie die Antwort an oder deaktivieren Sie die Option.'
    }
  },
  {
    nameEnglish: 'English - US',
    code: 'EN',
    countries: [
      'Australia',
      'Canada',
      'Egypt',
      'Ghana',
      'Hong Kong',
      'Ireland',
      'Kenya',
      'Malaysia',
      'New Zealand',
      'Nigeria',
      'Philippines',
      'Saudi Arabia',
      'Singapore',
      'South Africa',
      'UAE',
      'UK',
      'USA'
    ],
    translations: {
      submit: 'Submit',
      specify: 'Please specify',
      openAnswerError: 'You can’t save an empty answer option. Please specify the answer or uncheck the option.'
    }
  },
  {
    nameEnglish: 'Latin American Spanish',
    code: 'ES',
    countries: ['Colombia', 'Mexico', 'USA'],
    translations: {
      submit: 'Enviar',
      specify: 'Por favor especifique',
      openAnswerError: 'No puede guardar una opción de respuesta vacía. Por favor especifique la respuesta o desmarque la opción.'
    }
  },
  {
    nameEnglish: 'Spanish',
    code: 'ES-ES',
    countries: ['Spain'],
    translations: {
      submit: 'Enviar',
      specify: 'Por favor especifique',
      openAnswerError: 'No puede guardar una opción de respuesta vacía. Por favor, especifique la respuesta o desmarque la opción.'
    }
  },
  {
    nameEnglish: 'French',
    code: 'FR',
    countries: ['France', 'Morocco', 'Switzerland'],
    translations: {
      submit: 'Envoyer',
      specify: 'Veuillez spécifier',
      openAnswerError: `Vous ne pouvez pas sauvegarder une option de réponse vide. Veuillez spécifier la réponse ou décocher l'option.`
    }
  },
  {
    nameEnglish: 'Hebrew',
    code: 'HE',
    countries: ['Israel'],
    translations: { submit: 'הגש', specify: 'אנא ציין', openAnswerError: 'אינך יכול לשמור אפשרות תשובה ריקה. אנא ציין תשובה או ביטל את בחירת האפשרות.' },
    isRightToLeft: true
  },
  {
    nameEnglish: 'Hindi',
    code: 'HI',
    countries: ['India'],
    translations: {
      submit: 'सबमिट करें',
      specify: 'कृपया निर्दिष्ट करें',
      openAnswerError: 'आप एक खाली उत्तर विकल्प नहीं सहेज सकते। कृपया उत्तर निर्दिष्ट करें या विकल्प को अनचेक करें।'
    }
  },
  {
    nameEnglish: 'Indonesian',
    code: 'IND',
    countries: ['Indonesia'],
    translations: {
      submit: 'Kirim',
      specify: 'Harap tentukan',
      openAnswerError: 'Anda tidak dapat menyimpan opsi jawaban kosong. Harap tentukan jawaban atau hilangkan centang opsi tersebut.'
    }
  },
  {
    nameEnglish: 'Italian',
    code: 'IT',
    countries: ['Italy', 'Switzerland'],
    translations: {
      submit: 'Invia',
      specify: 'Si prega di specificare',
      openAnswerError: `Non puoi salvare un'opzione di risposta vuota. Si prega di specificare la risposta o deselezionare l'opzione.`
    }
  },
  {
    nameEnglish: 'Japanese',
    code: 'JA',
    countries: ['Japan'],
    translations: {
      submit: '送信',
      specify: '詳細を指定してください',
      openAnswerError: '空の回答オプションを保存することはできません。回答を指定するか、オプションのチェックを外してください。'
    }
  },
  {
    nameEnglish: 'Korean',
    code: 'KO',
    countries: ['South Korea'],
    translations: {
      submit: '제출',
      specify: '명시해 주세요',
      openAnswerError: '빈 답변 옵션을 저장할 수 없습니다. 답변을 명시하거나 옵션의 체크를 취소해 주세요.'
    }
  },
  {
    nameEnglish: 'Malay',
    code: 'MLY',
    countries: ['Malaysia'],
    translations: {
      submit: 'Hantar',
      specify: 'Sila nyatakan',
      openAnswerError: 'Anda tidak boleh menyimpan pilihan jawapan yang kosong. Sila nyatakan jawapan atau kosongkan pilihan tersebut.'
    }
  },
  {
    nameEnglish: 'Dutch',
    code: 'NL',
    countries: ['Netherlands'],
    translations: {
      submit: 'Verzenden',
      specify: 'Gelieve te specificeren',
      openAnswerError: 'Je kunt een leeg antwoordoptie niet opslaan. Gelieve het antwoord te specificeren of de optie uit te vinken.'
    }
  },
  {
    nameEnglish: 'Polish',
    code: 'PL',
    countries: ['Poland'],
    translations: {
      submit: 'Wyślij',
      specify: 'Proszę określić',
      openAnswerError: 'Nie możesz zapisać pustej opcji odpowiedzi. Proszę określić odpowiedź lub odznaczyć opcję.'
    }
  },
  {
    nameEnglish: 'Portuguese',
    code: 'PT',
    countries: ['Portugal'],
    translations: {
      submit: 'Enviar',
      specify: 'Por favor especifique',
      openAnswerError: 'Não é possível salvar uma opção de resposta vazia. Por favor, especifique a resposta ou desmarque a opção.'
    }
  },
  {
    nameEnglish: 'Brazilian Portuguese',
    code: 'PT-BR',
    countries: ['Brazil'],
    translations: {
      submit: 'Enviar',
      specify: 'Por favor, especifique',
      openAnswerError: 'Não é possível salvar uma opção de resposta vazia. Por favor, especifique a resposta ou desmarque a opção.'
    }
  },
  {
    nameEnglish: 'Romanian',
    code: 'ROU',
    countries: ['Romania'],
    translations: {
      submit: 'Trimiteți',
      specify: 'Vă rugăm să specificați',
      openAnswerError: 'Nu puteți salva o opțiune de răspuns goală. Vă rugăm să specificați răspunsul sau să debifați opțiunea.'
    }
  },
  {
    nameEnglish: 'Russian',
    code: 'RU',
    countries: ['Russia'],
    translations: {
      submit: 'Отправить',
      specify: 'Пожалуйста, укажите',
      openAnswerError: 'Невозможно сохранить пустой вариант ответа. Пожалуйста, укажите ответ или снимите флажок с опции.'
    }
  },
  {
    nameEnglish: 'Swedish',
    code: 'SV',
    countries: ['Sweden'],
    translations: {
      submit: 'Skicka',
      specify: 'Vänligen specificera',
      openAnswerError: 'Du kan inte spara ett tomt svarsalternativ. Vänligen specificera svaret eller avmarkera alternativet.'
    }
  },
  {
    nameEnglish: 'Tagalog',
    code: 'TG',
    countries: ['Philippines'],
    translations: {
      submit: 'I-submit',
      specify: 'Mangyaring tukuyin',
      openAnswerError: 'Hindi mo maaaring i-save ang isang walang sagot na opsyon. Mangyaring tukuyin ang sagot o i-uncheck ang opsyon.'
    }
  },
  {
    nameEnglish: 'Thai',
    code: 'TH',
    countries: ['Thailand'],
    translations: {
      submit: 'ส่ง',
      specify: 'กรุณาระบุ',
      openAnswerError: 'คุณไม่สามารถบันทึกตัวเลือกคำตอบที่ว่างเปล่าได้ กรุณาระบุคำตอบหรือยกเลิกการเลือกตัวเลือก'
    }
  },
  {
    nameEnglish: 'Turkish',
    code: 'TR',
    countries: ['Turkey'],
    translations: {
      submit: 'Gönder',
      specify: 'Lütfen belirtin',
      openAnswerError: 'Boş bir cevap seçeneği kaydedemezsiniz. Lütfen cevabı belirtin veya seçeneği kaldırın.'
    }
  },
  {
    nameEnglish: 'Taiwanese Mandarin Chinese',
    code: 'TW',
    countries: ['Taiwan'],
    translations: { submit: '提交', specify: '請指定', openAnswerError: '您無法儲存空白的回答選項。請指定回答或取消選擇該選項。' }
  },
  {
    nameEnglish: 'Vietnamese',
    code: 'VI',
    countries: ['Vietnam'],
    translations: {
      submit: 'Gửi',
      specify: 'Vui lòng chỉ định',
      openAnswerError: 'Bạn không thể lưu một lựa chọn câu trả lời trống. Vui lòng chỉ định câu trả lời hoặc bỏ chọn tùy chọn.'
    }
  },
  {
    nameEnglish: 'Chinese (Simplified)',
    code: 'ZH-S',
    countries: ['China', 'Malaysia'],
    translations: { submit: '提交', specify: '请指定', openAnswerError: '你不能保存空的回答选项。请指定回答或取消选择该选项。' }
  },
  {
    nameEnglish: 'Chinese (Traditional)',
    code: 'ZH-T',
    countries: ['Hong Kong'],
    translations: { submit: '遞交', specify: '請指定', openAnswerError: '你不能保存空的回答選項。請指定回答或取消選擇該選項。' }
  }
];
